import uniqid from "uniqid";

import { onViewLoad } from "jose_modern/on_view_load";
import { Modal } from "~/jose_modern/modal";
import { Flash } from "~/jose_modern/flash";
import { Confirm } from "~/jose_modern/confirm";
import { StashedFile } from "~/jose_modern/stashed_file";
import { stashedFilePath } from "routes";

import { AllergenInput } from "./common.js";

const MODAL_SELECTOR = "#facility-allergens-modal[data-mode='bulk']";

function setup() {
  const modal = document.querySelector(MODAL_SELECTOR);
  if (!modal) return;

  const updateUrl = modal.dataset.updateUrl;
  const editorRoot = modal.querySelector(".allergen-editor");
  const allergensSection = modal.querySelector("[data-section='allergens']");
  const prefixSection = modal.querySelector("[data-section='prefix']");
  const allergensToggle = modal.querySelector("[data-toggle='allergens']");
  const prefixToggle = modal.querySelector("[data-toggle='prefix']");
  const visibilityToggle = modal.querySelector("[data-toggle='visibility']");
  const prefixInput = modal.querySelector("[data-prefix-input]");
  const errorEl = modal.querySelector("[data-error]");
  const applyBtn = modal.querySelector("[data-action='apply']");
  const progress = modal.querySelector("[data-progress]");
  const progressBar = modal.querySelector("[data-progress-bar]");
  const progressMessage = modal.querySelector("[data-progress-message]");

  let collectedAllergens = {};
  let inFlight = false;

  new AllergenInput(editorRoot, (allergens) => {
    collectedAllergens = allergens;
  });

  function syncToggle(toggle, section) {
    section.classList.toggle("half-opacity", !toggle.checked);
    section.querySelectorAll("input").forEach((el) => {
      el.disabled = !toggle.checked;
    });
  }
  allergensToggle.addEventListener("change", () => syncToggle(allergensToggle, allergensSection));
  prefixToggle.addEventListener("change", () => syncToggle(prefixToggle, prefixSection));
  syncToggle(prefixToggle, prefixSection);

  modal.addEventListener("modal:open", () => {
    errorEl.classList.add("is-hidden");
    progress.classList.add("is-hidden");
  });

  /// Reflect job progress in the bar while we await StashedFile.download.
  document.addEventListener("stashed-file:progress", (ev) => {
    if (progress.classList.contains("is-hidden")) return;
    if (typeof ev.progressPercentage === "number") progressBar.value = ev.progressPercentage;
    if (ev.progressMessage) progressMessage.textContent = ev.progressMessage;
  });

  applyBtn.addEventListener("click", async () => {
    if (inFlight) return;
    errorEl.classList.add("is-hidden");
    if (!allergensToggle.checked && !prefixToggle.checked && !visibilityToggle.checked) {
      return showError("Choose what to update first.");
    }

    const raw = (modal.dataset.selectedRecipeIds || "").trim();
    const recipeIds = raw
      ? raw
          .split(",")
          .map(Number)
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];
    if (recipeIds.length === 0) return showError("Select recipes first.");

    const updateAllergens = allergensToggle.checked;
    const proposed = updateAllergens ? collectedAllergens : {};
    const prefix = prefixToggle.checked ? prefixInput.value : null;

    if (updateAllergens && AllergenInput.showIncompleteWarning(editorRoot)) return;

    if (updateAllergens && Object.keys(collectedAllergens).length === 0) {
      const result = await Confirm.confirm(
        "You checked 'Update facility allergens' but didn't select any. " +
          "Clear facility allergens on the selected recipes?",
        { confirm: "Clear facility allergens" }
      );
      if (!result) return;
    }

    inFlight = true;
    applyBtn.disabled = true;
    applyBtn.classList.add("is-loading");
    progressBar.removeAttribute("value");
    progressMessage.textContent = "Updating recipes...";
    progress.classList.remove("is-hidden");

    try {
      const response = await fetch(updateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          "recipe_ids": recipeIds,
          "update_allergens": updateAllergens,
          "proposed_allergens": proposed,
          prefix,
          "show_facility_allergens": visibilityToggle.checked,
          "stashed-file-client-id": uniqid(),
        }),
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        progress.classList.add("is-hidden");
        return showError(json.error || "Failed to start update.");
      }

      const { token } = await response.json();
      const result = await StashedFile.download({ path: stashedFilePath(token) });

      Flash.success(result.progressMessage || "Recipes updated.");
      Modal.hide("#facility-allergens-modal");
    } catch (err) {
      console.error(err);
      progress.classList.add("is-hidden");
      showError("Update failed. Please try again.");
    } finally {
      inFlight = false;
      applyBtn.disabled = false;
      applyBtn.classList.remove("is-loading");
    }
  });

  function showError(text) {
    errorEl.textContent = text;
    errorEl.classList.remove("is-hidden");
  }
}

onViewLoad(["users#show", "batch_downloads#show"], setup);
